import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'
  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'
  import ExpandableTextAreaInput from '@/components/common/ExpandableTextAreaInput.vue'
  import useEditor from '@/composables/useEditor'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useBreakpoint from '@/composables/useBreakpoint'
  import { EditorModule } from '@/store/modules/editor'
  import useCourse from '@/composables/useCourse'
  import { useRoute } from 'vue-router/composables'
  import type { PropType } from 'vue'
  import type { Nullable } from '@/services/interfaces/Content'
  import type {
    CourseModule,
    Lesson,
    CourseSectionMap,
    EditorStatePayload,
    FollowUp,
    EditorStateMap,
    LeuckentextQuiz,
  } from '@/services/interfaces/Course'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'TextRenderer',
  props: {
    source: {
      type: String,
      required: true,
    },
    formatContent: {
      type: Boolean,
      default: false,
    },
    showTextInput: {
      type: Boolean,
      default: false,
    },
    showTextArea: {
      type: Boolean,
      default: false,
    },
    isSmallText: {
      type: Boolean,
      default: false,
    },
    editTitle: String,
    editSubTitle: String,
    placeholder: String,
    showToolbarTextBlockOnly: {
      type: Boolean,
      default: false,
    },
    addImageShadow: {
      type: Boolean,
      default: false,
    },
    allowEdit: {
      default: '',
      type: String as PropType<'yes' | 'no' | ''>,
    },
    allowSourceChangesInEditor: Boolean,
    showExtraTextTools: Boolean,
    showBulletTextTools: Boolean,
    editorState: String as PropType<'currentLesson' | 'currentModule' | string>,
    editorProperty: String as PropType<
      keyof Lesson | keyof CourseModule | keyof FollowUp | keyof LeuckentextQuiz | string
    >,
    mutateAndQueueFunc: Function as PropType<(data: EditorStatePayload) => void>,
    canDiscardLocalChanges: {
      default: true,
      type: Boolean,
    },
    required: {
      type: Boolean,
      default: false,
    },
    isTextInputReadOnly: {
      type: Boolean,
      default: false,
    },
    skipGlossaryCheck: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['text-input'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const route = useRoute()
  const { inEditorMode: editMode, discardChanges, decodeAndParseSource, isDiscardingAllChanges } = useEditor(route)
  const { isLargeDesktop } = useBreakpoint()
  const {
    convertStringWithNewLineCharactersIntoArrayOfStrings,
    joinArrayOfStringsWithNewlineCharacter,
    locale,
    formatPhrase,
  } = useCommonMixin()
  const { currentLesson, currentModule } = useCourse()

  const editorEl = ref(null as Nullable<HTMLDivElement>)
  let editorInstance: Nullable<any> = null
  const editorKey = ref(Math.random().toString(36))
  const textInputKey = ref(Math.random().toString(36))
  const textAreaInputKey = ref(Math.random().toString(36))

  const inEditorMode = computed(() => {
    if (props.allowEdit !== '') {
      return isLargeDesktop.value && props.allowEdit === 'yes'
    }
    return isLargeDesktop.value && editMode.value
  })

  const parsedSource = computed(() => {
    if (props.showTextInput || props.showTextArea) return props.source
    return decodeAndParseSource(props.source)
  })

  const sourceContent = computed(() => (props.formatContent ? formatPhrase(props.source) : props.source))
  const textInput = ref<string>(parsedSource.value)

  watch(inEditorMode, (el) => {
    if (el) {
      reInitializeEditorInstance()
    }
  })

  watch(
    () => props.source,
    () => {
      textInput.value = parsedSource.value
      if (!(props.showTextInput || props.showTextArea) && props.allowSourceChangesInEditor) {
        editorInstance?.setData(textInput.value)
      }
    },
  )

  watch(editorKey, () => {
    if (!props.showTextArea && !props.showTextInput) {
      reInitializeEditorInstance()
    }
  })

  watch(textInput, (text) => {
    emit('text-input', text)
    if (props.editorProperty && props.editorState && !isDiscardingAllChanges.value && !props.showTextArea) {
      EditorModule.setEditableState({
        key: `${props.editorState}${props.editorProperty}`,
        path: route.path,
        state: props.editorState,
        property: props.editorProperty,
        value: props.showTextInput || props.showTextArea ? text : encodeURIComponent(text),
        mutateAndQueue: props.mutateAndQueueFunc,
      })
    }
  })

  const reInitializeEditorInstance = () => {
    nextTick(() => {
      editorInstance?.destroy()
      editorInstance = null
      initializeCKEditor()
    })
  }

  const initializeCKEditor = () => {
    if (editorEl.value && !editorInstance) {
      const { ClassicEditor } = window as any
      if (!ClassicEditor) return
      const toolbarConfig: any = {
        placeholder: props.placeholder || '',
        typing: {
          transformations: {
            include: [
              // Use only the 'quotes' and 'typography' groups.
              'quotes',
              'typography',
            ],
            extra: [
              // Add some custom transformations – e.g. for emojis.
              { from: ':)', to: '🙂' },
              { from: ':+1:', to: '👍' },
              { from: ':tada:', to: '🎉' },
            ],
          },
        },
        language: locale.value,
      }

      const toolbarItems = [
        'bold',
        'italic',
        'code',
        'link',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'codeBlock',
        'blockQuote',
        '|',
        'heading',
        'horizontalLine',
      ]
      if (props.showToolbarTextBlockOnly) {
        toolbarItems.splice(2)
      }
      if (props.showBulletTextTools) {
        toolbarItems.push('bulletedList', 'numberedList')
      }
      if (props.showExtraTextTools) {
        toolbarItems.push('blockQuote', 'heading')
      }

      toolbarConfig.toolbar = toolbarItems
      ClassicEditor.setup(editorEl.value, toolbarConfig).then((editor: any) => {
        editorInstance = editor

        editor.setData(parsedSource.value)
        editor.model.document.on('change:data', () => {
          textInput.value = editor.getData()
        })
      })
    }
  }

  const hasLocalChanges = computed(() => textInput.value !== parsedSource.value)

  const discardChangesCallback = (source?: string | EditorStatePayload, stateBeforeChange?: EditorStateMap) => {
    const originalSource =
      stateBeforeChange?.[props.editorState!]?.[props.editorProperty!] ??
      ((source as EditorStatePayload)?.value as CourseSectionMap)?.prevPropValue ??
      source
    if (typeof originalSource === 'string') {
      if (props.showTextInput || props.showTextArea) {
        textInput.value = originalSource
      } else {
        textInput.value = decodeAndParseSource(originalSource)
      }
    } else if (Array.isArray(originalSource)) {
      textInput.value = joinArrayOfStringsWithNewlineCharacter(originalSource)
    } else {
      textInput.value = parsedSource.value
    }
    // to re-render the text inputs and editor
    editorKey.value = Math.random().toString(36)
    textAreaInputKey.value = Math.random().toString(36)
    textAreaInputKey.value = Math.random().toString(36)
    if (!(props.showTextInput || props.showTextArea)) {
      editorInstance?.setData(textInput.value)
    }
  }

  const updateStateModel = (data: EditorStatePayload) => {
    let source = data.value
    if (Array.isArray(source)) source = joinArrayOfStringsWithNewlineCharacter(source)
    else if (typeof source !== 'string') source = data.apiPayload
    discardChangesCallback(source as string, {
      currentLesson: currentLesson.value,
      currentModule: currentModule.value as CourseModule,
      lessonSlideshows: EditorModule.lessonSlideshows,
    })
  }

  // TODO: investigate if we still need discard local changes
  const discardLocalChanges = () => {
    if (!props.canDiscardLocalChanges) {
      textInput.value = ''
      if (!(props.showTextInput || props.showTextArea)) {
        editorInstance?.setData(textInput.value)
      }
      return
    }
    discardChanges(() =>
      discardChangesCallback(undefined, {
        currentLesson: currentLesson.value,
        currentModule: currentModule.value as CourseModule,
        lessonSlideshows: EditorModule.lessonSlideshows,
      }),
    )
  }

  const onTextAreaInputChange = (text: string) => {
    if (props.editorProperty && props.editorState && !isDiscardingAllChanges.value) {
      EditorModule.setEditableState({
        key: `${props.editorState}${props.editorProperty}`,
        path: route.path,
        state: props.editorState,
        property: props.editorProperty,
        value: convertStringWithNewLineCharactersIntoArrayOfStrings(text),
        mutateAndQueue: props.mutateAndQueueFunc,
      })
    }
  }

  onBeforeUnmount(() => {
    editorInstance?.destroy()
    EditorModule.unsubscribe({ type: 'discard', callback: discardChangesCallback })
    EditorModule.unsubscribe({ type: 'save', callback: updateStateModel })
  })

  onMounted(() => {
    if (!props.editorState || !props.editorProperty) return

    EditorModule.subscribe({
      type: 'discard',
      key: `${props.editorState}${props.editorProperty}`,
      callback: () =>
        discardChangesCallback(textInput.value, {
          currentLesson: currentLesson.value,
          currentModule: currentModule.value as CourseModule,
          lessonSlideshows: EditorModule.lessonSlideshows,
        }),
    })

    EditorModule.subscribe({
      type: 'save',
      key: `${props.editorState}${props.editorProperty}`,
      callback: updateStateModel,
    })

    if (inEditorMode.value) {
      initializeCKEditor()
    }
  })

return { __sfc: true,props, emit, route, editMode, discardChanges, decodeAndParseSource, isDiscardingAllChanges, isLargeDesktop, convertStringWithNewLineCharactersIntoArrayOfStrings, joinArrayOfStringsWithNewlineCharacter, locale, formatPhrase, currentLesson, currentModule, editorEl, editorInstance, editorKey, textInputKey, textAreaInputKey, inEditorMode, parsedSource, sourceContent, textInput, reInitializeEditorInstance, initializeCKEditor, hasLocalChanges, discardChangesCallback, updateStateModel, discardLocalChanges, onTextAreaInputChange, computed, onBeforeUnmount, onMounted, ref, watch, nextTick, HTMLRenderer, ExpandableTextAreaInput, useEditor, useCommonMixin, useBreakpoint, EditorModule, useCourse, useRoute }
}

})